import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import {
  Loader2, MapPin, ShieldCheck, Users, Clock, Building2, Zap, Package,
  Link2, Info, AlertTriangle, CheckCircle2, ThumbsUp, MessageSquare, ThumbsDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const RESPONSE_META = {
  interested:      { title: "I'm Interested",   icon: ThumbsUp,      tone: "gold", detail: "SpotBounty will follow up to discuss terms and next steps." },
  request_changes: { title: "Request Changes",  icon: MessageSquare, tone: "amber", detail: "Tell us what you'd like adjusted." },
  not_interested:  { title: "Not a Fit Right Now", icon: ThumbsDown, tone: "red", detail: "We appreciate you letting us know." },
};

export default function ManagerView() {
  const { token } = useParams();
  const [loc, setLoc] = useState(null);
  const [error, setError] = useState(""); // "revoked" | "expired" | "invalid" | "network"
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", email: "", phone: "", comments: "" });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/l/${token}`);
        if (r.status === 404) { setError("invalid"); setLoading(false); return; }
        if (r.status === 410) {
          const d = await r.json().catch(() => ({}));
          setError(d?.detail === "revoked" ? "revoked" : "expired");
          setLoading(false); return;
        }
        if (!r.ok) { setError("network"); setLoading(false); return; }
        const d = await r.json();
        setLoc(d);
        if (d.response_recorded) setSubmitted(true);
      } catch { setError("network"); }
      setLoading(false);
    })();
  }, [token]);

  async function submitResponse() {
    if (!chosen) return;
    setFormError("");
    if (!form.name.trim() || !form.email.trim()) {
      setFormError("Name and email are required."); return;
    }
    setSubmitting(true);
    const r = await fetch(`${API}/api/l/${token}/response`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ response: chosen, ...form }),
    });
    setSubmitting(false);
    if (r.status === 409) { setSubmitted(true); return; }
    if (r.status === 410) {
      const d = await r.json().catch(() => ({}));
      setError(d?.detail === "revoked" ? "revoked" : "expired");
      return;
    }
    if (!r.ok) { setFormError("Could not submit — please try again."); return; }
    setSubmitted(true);
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <Seo title="Location Review" description="SpotBounty proposed vending location review" path={`/l/${token}`} noindex />

      <header className="border-b border-[#2A2F34]">
        <div className="max-w-3xl mx-auto px-5 md:px-6 py-4 flex items-center justify-between">
          <SBLogo size={18} />
          <div className="flex items-center gap-2 border border-[#FFC107]/40 bg-[#FFC107]/5 rounded-full px-3 py-1.5">
            <Link2 size={11} className="text-[#FFC107]" />
            <span className="text-[10.5px] font-bold uppercase tracking-widest text-[#FFC107]">Secure Manager Link</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-6 py-8 space-y-6">
        {loading && (
          <div className="text-[#A1A1AA] flex items-center gap-2 py-8"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        )}

        {error && (
          <div className="sb-card p-6 border-[#EF4444]/40 flex items-start gap-3" style={{ background: "rgba(239,68,68,.05)" }} data-testid="mgr-error">
            <AlertTriangle size={18} className="text-[#EF4444] mt-0.5" />
            <div>
              <div className="text-[14px] font-black text-[#EF4444]">
                {error === "revoked" && "This link has been revoked."}
                {error === "expired" && "This link has expired."}
                {error === "invalid" && "This link is invalid."}
                {error === "network" && "Could not load."}
              </div>
              <div className="text-[12px] text-[#A1A1AA] mt-1">
                {error === "expired" && "Contact your SpotBounty scout for a new link."}
                {error === "revoked" && "The scout or SpotBounty ended this review session. Contact your SpotBounty scout to reopen."}
                {(error === "invalid" || error === "network") && "If you believe this is a mistake, contact the SpotBounty team."}
              </div>
            </div>
          </div>
        )}

        {loc && !error && (
          <>
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl border border-[#2A2F34]" style={{
              background: "linear-gradient(140deg, #15191D 0%, #0B0E11 60%, #080B0D 100%)",
            }}>
              <div className="absolute inset-0 opacity-[.12] pointer-events-none" style={{
                backgroundImage: "radial-gradient(circle at 15% 20%, rgba(255,193,7,.7) 0, transparent 45%), radial-gradient(circle at 85% 90%, rgba(255,193,7,.4) 0, transparent 55%)",
              }} />
              <div className="relative p-6 md:p-8">
                <div className="sb-eyebrow">Proposed Vending Opportunity</div>
                <h1 className="mt-2 text-[28px] md:text-[36px] font-black leading-tight" data-testid="mgr-business-name">{loc.business_name}</h1>
                <div className="mt-3 text-[13px] text-[#A1A1AA] flex items-start gap-2">
                  <MapPin size={14} className="mt-0.5 shrink-0" />
                  <div>{[loc.address_line1, loc.city && `${loc.city}${loc.state ? `, ${loc.state}` : ""}`, loc.zip].filter(Boolean).join(" · ") || "Location on file"}</div>
                </div>
              </div>
            </div>

            {/* Explainer */}
            <div className="sb-card p-5 flex items-start gap-3 border-[#3B82F6]/30" style={{ background: "rgba(59,130,246,.04)" }}>
              <Info size={16} className="text-[#3B82F6] mt-0.5 shrink-0" />
              <div className="text-[13px] leading-relaxed">
                A SpotBounty Scout submitted this location as a potential vending opportunity for your business. This link was shared with you securely — <b className="text-white">your contact info is not visible to the Scout</b>.
              </div>
            </div>

            {/* Summary */}
            <section className="sb-card p-5 md:p-6">
              <div className="sb-eyebrow">Opportunity Summary</div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SummaryItem icon={Building2} label="On-site Employees" value={loc.employees} />
                <SummaryItem icon={Users} label="Est. Visitors / Day" value={loc.visitors} />
                <SummaryItem icon={Clock} label="Hours" value={loc.hours} />
                <SummaryItem icon={Clock} label="Shifts" value={loc.shifts ? `${loc.shifts} shift${loc.shifts > 1 ? "s" : ""}` : null} />
                <SummaryItem icon={Zap} label="Open 24/7" value={loc.open_24_7 ? "Yes" : "No"} />
                <SummaryItem icon={Package} label="Current Vending" value={loc.current_vending || "None on file"} />
              </div>
            </section>

            <section className="sb-card p-5 md:p-6">
              <div className="sb-eyebrow">Proposed Equipment &amp; Terms</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {(loc.desired_machine_types || []).length > 0
                  ? (loc.desired_machine_types || []).map((m) => <span key={m} className="sb-pill sb-pill-gold">{m}</span>)
                  : <span className="text-[13px] text-[#52525b]">Machine types will be confirmed with SpotBounty.</span>}
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-[#52525b]">Preferred Contract Term</div>
                  <div className="mt-1 text-[14px] text-white/90">{loc.preferred_term_months ? `${loc.preferred_term_months} months` : <span className="text-[#52525b]">To be discussed</span>}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-[#52525b]">Exclusivity</div>
                  <div className="mt-1 text-[14px] text-white/90 capitalize">{loc.exclusivity_pref ? loc.exclusivity_pref.replace(/_/g, " ") : <span className="text-[#52525b]">To be discussed</span>}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-[#52525b]">Link Valid Until</div>
                  <div className="mt-1 text-[14px] text-white/90">{new Date(loc.expires_at).toLocaleString()}</div>
                </div>
              </div>
              <div className="mt-4 text-[11.5px] text-[#F59E0B]">
                Reference details only — final equipment and placement confirmed with you before any install.
              </div>
            </section>

            {/* Response */}
            {submitted ? (
              <section className="sb-card p-6 border-[#22C55E]/40 flex items-start gap-3" style={{ background: "rgba(34,197,94,.05)" }} data-testid="mgr-thanks">
                <CheckCircle2 size={20} className="text-[#22C55E] mt-0.5" />
                <div>
                  <div className="text-[15px] font-black">Thanks — your response is recorded.</div>
                  <div className="text-[12.5px] text-[#A1A1AA] mt-1">SpotBounty has been notified. Someone will be in touch shortly.</div>
                </div>
              </section>
            ) : (
              <section className="sb-card p-5 md:p-6">
                <div className="sb-eyebrow">Your response</div>
                <div className="text-[13px] text-[#A1A1AA] mt-1">Tell us how you&apos;d like to move forward.</div>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {Object.entries(RESPONSE_META).map(([key, r]) => {
                    const Icon = r.icon;
                    const active = chosen === key;
                    const toneClass = {
                      gold:  active ? "border-[#FFC107] bg-[#FFC107]/10 text-[#FFC107]" : "border-[#2A2F34] text-white hover:border-[#FFC107]",
                      amber: active ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]" : "border-[#2A2F34] text-white hover:border-[#F59E0B]",
                      red:   active ? "border-[#EF4444] bg-[#EF4444]/10 text-[#EF4444]" : "border-[#2A2F34] text-white hover:border-[#EF4444]",
                    }[r.tone];
                    return (
                      <button key={key} onClick={() => setChosen(key)}
                              className={`text-left rounded-lg border p-4 transition-colors ${toneClass}`}
                              data-testid={`mgr-choose-${key}`}>
                        <Icon size={16} />
                        <div className="mt-2 text-[13px] font-black">{r.title}</div>
                        <div className="mt-1 text-[11.5px] opacity-80">{r.detail}</div>
                      </button>
                    );
                  })}
                </div>

                {chosen && (
                  <div className="mt-5 space-y-3" data-testid="mgr-response-form">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="block"><div className="sb-label">Your name *</div>
                        <input className="sb-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mgr-name" /></label>
                      <label className="block"><div className="sb-label">Title</div>
                        <input className="sb-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mgr-title" /></label>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="block"><div className="sb-label">Email *</div>
                        <input type="email" className="sb-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="mgr-email" /></label>
                      <label className="block"><div className="sb-label">Phone</div>
                        <input className="sb-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="mgr-phone" /></label>
                    </div>
                    <label className="block"><div className="sb-label">Comments {chosen === "request_changes" && <span className="text-[#F59E0B]">(what would you change?)</span>}</div>
                      <textarea rows={4} className="sb-input" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} data-testid="mgr-comments" /></label>
                    {formError && <div className="text-[12px] text-[#EF4444]" data-testid="mgr-form-error">{formError}</div>}
                    <button disabled={submitting} onClick={submitResponse} className="sb-btn-primary w-full text-[12px]" style={{ padding: "14px 16px" }} data-testid="mgr-submit">
                      {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Send Response
                    </button>
                  </div>
                )}
              </section>
            )}

            <section className="sb-card p-5 flex items-start gap-3 border-[#22C55E]/30" style={{ background: "rgba(34,197,94,.04)" }}>
              <ShieldCheck size={18} className="text-[#22C55E] mt-0.5 shrink-0" />
              <div className="text-[13px] leading-relaxed">
                SpotBounty will follow up with you directly to finalize terms and equipment. This review page is view-only — <b className="text-white">no signature required yet</b>.
              </div>
            </section>

            <div className="text-[11px] text-[#52525b] text-center pt-4 border-t border-[#2A2F34]">
              <div>SpotBounty · Connecting businesses with independent vending operators.</div>
              <div className="mt-1"><Link to="/" className="hover:text-white">spotbounty.com</Link></div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function SummaryItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg border border-[#2A2F34] bg-[#FFC107]/5 flex items-center justify-center shrink-0">
        <Icon size={14} className="text-[#FFC107]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10.5px] uppercase tracking-widest text-[#52525b]">{label}</div>
        <div className="mt-0.5 text-[14px] font-bold text-white/95 truncate">{value == null || value === "" ? <span className="text-[#52525b] font-normal">—</span> : String(value)}</div>
      </div>
    </div>
  );
}
